import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { useAuth } from '@/hooks/useAuth';
import { refreshStoreAfter } from '../storeRefresh';

export interface StockCell {
    variantId: string;
    size: string | null;
    available: number | null;
    committed: number;
    total: number | null;
    editable: boolean;
}

export interface StockProduct {
    id: string;
    slug: string;
    name: string;
    kind: 'garment' | 'pack' | 'lot';
    cells: StockCell[];
}

export interface PendingLot {
    orderId: string;
    orderNumber: string;
    itemId: string;
    status: string;
    notes: string | null;
}

export interface StockMovement {
    id: string;
    variantId: string;
    productId: string;
    size: string | null;
    quantity: number;
    reason: string;
    motivo: string | null;
    note: string | null;
    orderNumber: string | null;
    createdAt: string;
}

export interface OversellRow {
    variantId: string;
    size: string | null;
    productName: string;
    counted: number;
    committed: number;
    shortfall: number;
    orders: Array<{ orderNumber: string; quantity: number; status: string }>;
}

export interface StockMatrix {
    enforced: boolean;
    threshold: number;
    hasInitialCount: boolean;
    products: StockProduct[];
    pendingLots: PendingLot[];
    movements: StockMovement[];
}

export interface CountResult {
    ok: boolean;
    oversell?: OversellRow[];
}

const KEY = ['admin', 'merch-stock'] as const;

function num(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function asMatrix(raw: Partial<StockMatrix> | undefined): StockMatrix {
    const products = Array.isArray(raw?.products) ? raw.products : [];
    return {
        enforced: raw?.enforced === true,
        threshold: num(raw?.threshold ?? 2),
        hasInitialCount: raw?.hasInitialCount === true,
        products: products.map((product) => ({
            ...product,
            cells: (Array.isArray(product.cells) ? product.cells : []).map((cell) => ({
                ...cell,
                available: cell.available == null ? null : num(cell.available),
                committed: num(cell.committed),
                total: cell.total == null ? null : num(cell.total),
                editable: cell.editable === true,
            })),
        })),
        pendingLots: Array.isArray(raw?.pendingLots) ? raw.pendingLots : [],
        movements: Array.isArray(raw?.movements) ? raw.movements : [],
    };
}

export function useMerchStock() {
    const { user } = useAuth();
    return useQuery({
        queryKey: KEY,
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_store_merch_stock_matrix', {
                p_admin_user_id: user!.id,
            });
            if (error) throw new Error(error.message);
            return asMatrix(normalizeRpcResponse(data)[0] as Partial<StockMatrix> | undefined);
        },
    });
}

export function useMerchStockAlerts() {
    const { user } = useAuth();
    return useQuery({
        queryKey: [...KEY, 'alerts'],
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_store_merch_stock_alerts', {
                p_admin_user_id: user!.id,
            });
            if (error) throw new Error(error.message);
            const row = normalizeRpcResponse(data)[0] as { lowOrOut?: number; lotPending?: number } | undefined;
            return { lowOrOut: num(row?.lowOrOut), lotPending: num(row?.lotPending) };
        },
    });
}

function useStockMutation<TInput, TResult = unknown>(fn: (userId: string, input: TInput) => Promise<TResult>) {
    const { user } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: TInput) => {
            if (!user?.id) throw new Error('Sin sesión admin');
            return fn(user.id, input);
        },
        onSuccess: () => refreshStoreAfter(qc, 'merch-stock'),
    });
}

async function call(name: string, params: Record<string, unknown>) {
    const { data, error } = await dbClient.rpc(name, params);
    if (error) throw new Error(error.message);
    return normalizeRpcResponse(data)[0];
}

export function useSetStockEnforced() {
    return useStockMutation((userId: string, enforced: boolean) => call('admin_set_store_stock_enforced', {
        p_admin_user_id: userId,
        p_enforced: enforced,
    }));
}

export function useSetStockThreshold() {
    return useStockMutation((userId: string, threshold: number) => call('admin_set_store_stock_threshold', {
        p_admin_user_id: userId,
        p_threshold: threshold,
    }));
}

export function useAdjustStock() {
    return useStockMutation((userId: string, input: {
        variantId: string;
        onHand: number;
        motivo: string;
        note?: string;
    }) => call('admin_adjust_store_stock', {
        p_admin_user_id: userId,
        p_variant_id: input.variantId,
        p_on_hand: input.onHand,
        p_motivo: input.motivo,
        p_note: input.note ?? '',
    }));
}

export function useInitialStockCount() {
    return useStockMutation(async (userId: string, input: { counts: string; acceptOversell: boolean }) => {
        const row = await call('admin_initial_store_stock_count', {
            p_admin_user_id: userId,
            p_counts: input.counts,
            p_accept_oversell: input.acceptOversell,
        });
        return (row ?? { ok: false }) as CountResult;
    });
}

export function useAssignLotLine() {
    return useStockMutation((userId: string, input: { itemId: string; sizes: string }) => call('admin_assign_lot_line', {
        p_admin_user_id: userId,
        p_item_id: input.itemId,
        p_sizes: input.sizes,
    }));
}
